import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Input,
  Form,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";

import { FaCheckCircle, FaTimesCircle, FaWifi } from "react-icons/fa";
import "../css/CardScan.css";
import PaymentService from "../../../services/OrderingServices/PaymentService";
import { useNavigate } from "react-router-dom";

const CardScanModal = ({
  isOpen,
  toggle,
  orgCode,
  paymentData,
  onSuccess,
  setPayload,
  payload,
}) => {
  const navigate = useNavigate();

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(10);

  const inputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const handleCardPayment = async () => {
    try {
      setStatus("loading");

      const response = await PaymentService.payWithCard(payload, orgCode);

      setPaymentResponse(response);
      setStatus("success");
    } catch (error) {
      console.error(error);
      setErrorMessage(
        error?.response?.data?.error?.message || "Payment failed",
      );
      setStatus("error");
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setPaymentResponse(null);
    setErrorMessage("");
    setCountdown(10);
    toggle();
    navigate(`/${orgCode}`);
  };

  const handleRetry = () => {
    setStatus("idle");
    setErrorMessage("");
    setPaymentResponse(null);
    setPayload((prev) => ({ ...prev, cardUid: "" }));

    setTimeout(() => {
      inputRef.current?.focus();
    }, 200);
  };

  // ✅ Auto fill payload
  useEffect(() => {
    if (paymentData) {
      setPayload((prev) => ({
        ...prev,
        amount: paymentData.amount,
        referenceId: paymentData.referenceId,
        kioskId: paymentData.kioskId,
      }));
    }
  }, [paymentData]);

  // ✅ Focus input
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  // ✅ Success callback
  useEffect(() => {
    if (status === "success" && onSuccess) {
      onSuccess();
    }
  }, [status]);

  // ✅ Countdown + Auto Close
  useEffect(() => {
    let timer;

    if (status === "success" || status === "error") {
      setCountdown(10);

      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [status]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {status === "idle" && "Tap Card to Pay"}
        {status === "loading" && "Processing Payment..."}
        {status === "success" && "Payment Successful"}
        {status === "error" && "Payment Failed"}
      </ModalHeader>

      <ModalBody className="text-center">
        {/* IDLE */}
        {status === "idle" && (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-4">
              <div
                className="rfid-circle mb-3"
                style={{ transition: "all 0.3s ease" }}
              >
                {payload.cardUid ? (
                  <FaCheckCircle size={50} className="text-success rfid-icon" />
                ) : (
                  <FaWifi size={40} className="text-primary rfid-icon" />
                )}
              </div>

              <h5 className="mt-3">
                {payload.cardUid ? "Card Detected!" : "Waiting for Card..."}
              </h5>
              <p className="text-muted small">
                {payload.cardUid
                  ? "Processing your payment..."
                  : "Please tap your RFID card on the reader to complete payment."}
              </p>

              <FormGroup>
                <Label>Card UID</Label>
                <Input
                  innerRef={inputRef}
                  name="cardUid"
                  value={payload.cardUid}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value.length >= 11) {
                      handleCardPayment();
                    }
                  }}
                  style={{
                    fontSize: payload.cardUid ? "2.5rem" : "1rem",
                    textAlign: "center",
                    letterSpacing: "2px",
                    padding: "0.75rem",
                    fontWeight: "bold",
                    borderColor: payload.cardUid ? "#28a745" : "#ced4da",
                    boxShadow: payload.cardUid
                      ? "0 0 10px rgba(40, 167, 69, 0.5)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label for="amount">Amount</Label>
                <Input
                  type="number"
                  name="amount"
                  value={payload.amount}
                  onChange={handleChange}
                  readOnly
                  style={{
                    fontSize: "1.5rem",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                />
              </FormGroup>
            </CardBody>
          </Card>
        )}

        {/* LOADING */}
        {status === "loading" && (
          <div className="py-5">
            <Spinner color="primary" />
            <p className="mt-2">Processing payment...</p>
          </div>
        )}

        {/* SUCCESS */}
        {status === "success" && paymentResponse && (
          <div className="py-3">
            <FaCheckCircle size={80} className="text-success mb-3" />
            <h4 className="text-success fw-bold">Payment Completed</h4>

            <p className="text-muted">Your payment was successful.</p>

            <div className="mt-3">
              <strong>Transaction ID:</strong>{" "}
              {paymentResponse.data?.transactionId}
            </div>

            <div>
              <strong>Amount:</strong> Rs. {paymentResponse.data?.amount}
            </div>

            <div>
              <strong>Balance:</strong> Rs. {paymentResponse.data?.balanceAfter}
            </div>

            {/* Countdown */}
            <p className="text-muted mt-3">
              Closing in <strong>{countdown}</strong> seconds...
            </p>
          </div>
        )}

        {/* ERROR */}
        {status === "error" && (
          <div className="py-5">
            <FaTimesCircle size={70} className="text-danger mb-3" />

            <h4 className="text-danger fw-bold">Payment Failed</h4>

            <p className="text-muted">
              {errorMessage || "Something went wrong"}
            </p>

            <div className="d-flex justify-content-center gap-3 mt-3">
              <button className="btn btn-danger" onClick={handleRetry}>
                Try Again
              </button>

              <button
                className="btn btn-outline-secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>

            {/* Countdown */}
            <p className="text-muted mt-3">
              Auto closing in <strong>{countdown}</strong> seconds...
            </p>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-0">
        {status === "idle" && (
          <div className="d-flex gap-2 w-100">
            <Button
              color="success"
              className="w-100"
              onClick={handleCardPayment}
            >
              Confirm
            </Button>
            <Button color="danger" className="w-100" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        )}

        {status === "success" && (
          <Button color="primary" onClick={handleClose}>
            Done
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CardScanModal;
